// apps/office/src/pages/PartnerAttendance.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import {
  Users, UserCheck, UserX, Calendar, Filter,
  ChevronDown, ChevronRight, Loader2, Search,
  TrendingUp, Clock, Building2, User
} from 'lucide-react';
import pb from '../../lib/pocketbaseClient';

// Helper: ??????? ????? ???? ??? (Daily/Weekly/Monthly/Total)
const getDateRange = (filter) => {
  const now = new Date();
  const start = new Date(now);
  const end = new Date(now);
  switch (filter) {
    case 'daily':
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
      break;
    case 'weekly':
      const day = now.getDay(); // 0=Sun, 1=Mon, ...
      const diff = (day === 0 ? 6 : day - 1);
      start.setDate(now.getDate() - diff);
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
      break;
    case 'monthly':
      start.setDate(1);
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
      break;
    case 'total':
    default:
      return { start: null, end: null };
  }
  return { start: start.toISOString(), end: end.toISOString() };
};

// Helper: ???????????? ????????? ?????
const getStatusBadge = (checkIn, checkOut) => {
  if (!checkIn) return { label: 'Absent', color: 'bg-red-100 text-red-700 border-red-200' };
  if (checkOut) return { label: 'Present', color: 'bg-green-100 text-green-700 border-green-200' };
  return { label: 'Working', color: 'bg-amber-100 text-amber-700 border-amber-200' };
};

export default function PartnerAttendance() {
  const { user } = useAuth();

  // --- State ---
  const [loading, setLoading] = useState(true);
  const [labourList, setLabourList] = useState([]);
  const [attendanceMap, setAttendanceMap] = useState({}); // labourId -> array of attendance records
  const [expandedVendor, setExpandedVendor] = useState(null);
  const [attendanceFilter, setAttendanceFilter] = useState('daily');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  // --- Data Fetching (??? Attendance-?? ???) ---
  useEffect(() => {
    const fetchData = async () => {
      try {
        // 1. Partner ?????
        const partners = await pb.collection('partners').getFullList({
          filter: `createdBy = "${user.id}" || email = "${user.email}"`,
        });
        if (!partners.length) {
          setLoading(false);
          return;
        }
        const partner = partners[0];

        // 2. Active Site Assignments
        const assignments = await pb.collection('partner_site_assignments').getFullList({
          filter: `partner = "${partner.id}" && isActive = true`,
          expand: 'site',
        });
        const sites = assignments.map(a => a.expand?.site).filter(Boolean);
        const siteIds = sites.map(s => s.id);

        if (!siteIds.length) {
          setLoading(false);
          return;
        }

        // 3. Labour (???? active status-??, vendor ? site ??????????? ??)
        const labour = await pb.collection('labour').getFullList({
          filter: `site ~ "${siteIds.join('||')}" && status = "active"`,
          expand: 'vendor,site',
          sort: 'name',
        });
        setLabourList(labour);

        // 4. ?? ???????????? ?????? (????? ??????? ???? � ?????? ??????? ????)
        const labourIds = labour.map(l => l.id);
        let allAttendance = [];
        if (labourIds.length) {
          allAttendance = await pb.collection('labour_attendance').getFullList({
            filter: `labour ~ "${labourIds.join('||')}"`,
            sort: '-date',
          });
        }

        // 5. ????? ???? (labourId -> array of records)
        const map = {};
        labourIds.forEach(id => { map[id] = []; });
        allAttendance.forEach(rec => {
          if (map[rec.labour]) {
            map[rec.labour].push(rec);
          }
        });
        setAttendanceMap(map);
      } catch (error) {
        console.error('Error fetching attendance data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (user) fetchData();
  }, [user]);

  // --- Derived Data (????????? ????? ???????) ---
  const selectedDateStr = selectedDate; // YYYY-MM-DD

  // ??????? ??????? ???? ????????? ??????? ???????????? ??????
  const selectedDateStatus = useMemo(() => {
    const status = {};
    labourList.forEach(lab => {
      const records = attendanceMap[lab.id] || [];
      const record = records.find(r => r.date?.slice(0, 10) === selectedDateStr);
      if (record) {
        status[lab.id] = {
          check_in: record.check_in,
          check_out: record.check_out,
          id: record.id,
        };
      } else {
        status[lab.id] = null;
      }
    });
    return status;
  }, [labourList, attendanceMap, selectedDateStr]);

  const totalLabour = labourList.length;
  const workingNow = labourList.filter(lab => {
    const st = selectedDateStatus[lab.id];
    return st && st.check_in && !st.check_out;
  }).length;
  const presentToday = labourList.filter(lab => {
    const st = selectedDateStatus[lab.id];
    return st && st.check_out;
  }).length;
  const absentToday = labourList.filter(lab => !selectedDateStatus[lab.id]).length;
  const attendanceRate = totalLabour ? Math.round((presentToday / totalLabour) * 100) : 0;

  // ?????? ??????? ???????
  const vendorGroups = useMemo(() => {
    const groups = {};
    labourList.forEach(lab => {
      const vendor = lab.expand?.vendor;
      if (!vendor) {
        if (!groups['unassigned']) groups['unassigned'] = { vendor: null, labour: [] };
        groups['unassigned'].labour.push(lab);
      } else {
        if (!groups[vendor.id]) groups[vendor.id] = { vendor, labour: [] };
        groups[vendor.id].labour.push(lab);
      }
    });
    return Object.values(groups);
  }, [labourList]);

  // ????? ???????
  const filteredVendors = useMemo(() => {
    if (!searchTerm) return vendorGroups;
    return vendorGroups.filter(group => {
      const name = group.vendor?.name || 'Unassigned';
      return name.toLowerCase().includes(searchTerm.toLowerCase());
    });
  }, [vendorGroups, searchTerm]);

  // ????????? ??????? ???????????? ?????? (??????? ???????)
  const getFilteredAttendance = (labourId) => {
    const records = attendanceMap[labourId] || [];
    if (attendanceFilter === 'total') return records;
    const { start, end } = getDateRange(attendanceFilter);
    if (!start) return records;
    return records.filter(rec => {
      const d = new Date(rec.date);
      return d >= new Date(start) && d <= new Date(end);
    });
  };

  // --- Loading ---
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6">

      {/* Header */}
      <div className="flex flex-wrap justify-between items-start gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Users className="w-6 h-6 text-amber-500" />
            Labour & Attendance
          </h1>
          <p className="text-slate-500 text-sm">Monitor your workforce and attendance across sites</p>
        </div>
        <div className="flex items-center gap-2 bg-white border rounded-lg px-3 py-1.5 shadow-sm">
          <Calendar className="w-4 h-4 text-slate-400" />
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="border-0 focus:outline-none text-sm py-1"
          />
        </div>
      </div>

      {/* Summary Cards (????????? ??????? ??? ?????? ???) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-5 flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Total Labour</p>
              <p className="text-2xl font-bold text-slate-800">{totalLabour}</p>
            </div>
            <div className="p-3 bg-blue-50 rounded-full">
              <Users className="w-6 h-6 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5 flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Active / Working</p>
              <p className="text-2xl font-bold text-amber-600">{workingNow}</p>
            </div>
            <div className="p-3 bg-amber-50 rounded-full">
              <Clock className="w-6 h-6 text-amber-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5 flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Present</p>
              <p className="text-2xl font-bold text-green-600">{presentToday}</p>
            </div>
            <div className="p-3 bg-green-50 rounded-full">
              <UserCheck className="w-6 h-6 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5 flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Absent</p>
              <p className="text-2xl font-bold text-red-600">{absentToday}</p>
            </div>
            <div className="p-3 bg-red-50 rounded-full">
              <UserX className="w-6 h-6 text-red-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5 flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Attendance Rate</p>
              <p className="text-2xl font-bold text-indigo-600">{attendanceRate}%</p>
            </div>
            <div className="p-3 bg-indigo-50 rounded-full">
              <TrendingUp className="w-6 h-6 text-indigo-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search & Filter */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search vendor..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="flex items-center gap-2 bg-slate-100 p-1 rounded-lg">
          {['daily', 'weekly', 'monthly', 'total'].map((f) => (
            <button
              key={f}
              onClick={() => setAttendanceFilter(f)}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                attendanceFilter === f
                  ? 'bg-white shadow text-slate-800'
                  : 'text-slate-600 hover:bg-white/50'
              }`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Vendor Accordion */}
      <div className="space-y-3">
        {filteredVendors.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
            <Users className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <h3 className="text-lg font-semibold text-slate-700">No vendors found</h3>
            <p className="text-slate-400 text-sm">Try adjusting your search</p>
          </div>
        ) : (
          filteredVendors.map((group) => {
            const vendor = group.vendor;
            const vendorName = vendor?.name || 'Unassigned';
            const labourCount = group.labour.length;
            const isExpanded = expandedVendor === (vendor?.id || 'unassigned');

            return (
              <div key={vendor?.id || 'unassigned'} className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                {/* Vendor Header */}
                <div
                  className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50 transition-colors"
                  onClick={() => setExpandedVendor(isExpanded ? null : (vendor?.id || 'unassigned'))}
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-indigo-50 rounded-full">
                      <User className="w-5 h-5 text-indigo-500" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-800">{vendorName}</h3>
                      <p className="text-xs text-slate-500">{labourCount} labour{labourCount !== 1 ? 's' : ''}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge variant="outline" className="text-xs">
                      {labourCount} members
                    </Badge>
                    {isExpanded ? (
                      <ChevronDown className="w-5 h-5 text-slate-400" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-slate-400" />
                    )}
                  </div>
                </div>

                {/* Expanded Content */}
                {isExpanded && (
                  <div className="border-t border-slate-100 p-4 bg-slate-50/50">
                    {group.labour.length === 0 ? (
                      <p className="text-sm text-slate-400">No labour assigned</p>
                    ) : (
                      <div className="space-y-4">
                        {group.labour.map((lab) => {
                          const filteredAtt = getFilteredAttendance(lab.id);
                          // ????????? ??????? ?????????
                          const st = selectedDateStatus[lab.id];
                          const { label, color } = getStatusBadge(st?.check_in, st?.check_out);

                          // ?????????? (?????????? ?????? ????)
                          const totalPresent = filteredAtt.filter(r => r.check_out).length;
                          const totalWorking = filteredAtt.filter(r => r.check_in && !r.check_out).length;
                          const totalAbsent = filteredAtt.filter(r => !r.check_in).length;

                          return (
                            <div key={lab.id} className="bg-white rounded-lg border border-slate-200 p-4 shadow-sm">
                              <div className="flex flex-wrap items-start justify-between gap-2">
                                <div>
                                  <h4 className="font-medium text-slate-800 flex items-center gap-2">
                                    {lab.name}
                                    <Badge className={color}>{label}</Badge>
                                  </h4>
                                  {/* ????????? ??????? ???-??/??? ??? */}
                                  {st && (
                                    <div className="text-xs text-slate-500 mt-1">
                                      {st.check_in && <span>Check-in: {new Date(st.check_in).toLocaleTimeString()}</span>}
                                      {st.check_out && <span> | Check-out: {new Date(st.check_out).toLocaleTimeString()}</span>}
                                    </div>
                                  )}
                                  <div className="flex flex-wrap gap-2 mt-1">
                                    <Badge className="text-xs bg-green-50 text-green-700">
                                      Present: {totalPresent}
                                    </Badge>
                                    <Badge className="text-xs bg-amber-50 text-amber-700">
                                      Working: {totalWorking}
                                    </Badge>
                                    <Badge className="text-xs bg-red-50 text-red-700">
                                      Absent: {totalAbsent}
                                    </Badge>
                                  </div>
                                </div>
                                <div className="text-xs text-slate-400">
                                  {filteredAtt.length} records ({attendanceFilter})
                                </div>
                              </div>

                              {/* ???????????? ????? */}
                              {filteredAtt.length > 0 && (
                                <div className="mt-3 overflow-x-auto">
                                  <table className="w-full text-xs">
                                    <thead className="bg-slate-50 text-slate-600">
                                      <tr>
                                        <th className="px-3 py-2 text-left">Date</th>
                                        <th className="px-3 py-2 text-left">Check In</th>
                                        <th className="px-3 py-2 text-left">Check Out</th>
                                        <th className="px-3 py-2 text-left">Status</th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                      {filteredAtt.slice(0, 10).map((rec) => {
                                        const { label, color } = getStatusBadge(rec.check_in, rec.check_out);
                                        return (
                                          <tr key={rec.id} className="hover:bg-slate-50">
                                            <td className="px-3 py-2">{rec.date?.slice(0, 10)}</td>
                                            <td className="px-3 py-2">
                                              {rec.check_in ? new Date(rec.check_in).toLocaleTimeString() : '�'}
                                            </td>
                                            <td className="px-3 py-2">
                                              {rec.check_out ? new Date(rec.check_out).toLocaleTimeString() : '�'}
                                            </td>
                                            <td className="px-3 py-2">
                                              <Badge className={color}>{label}</Badge>
                                            </td>
                                          </tr>
                                        );
                                      })}
                                      {filteredAtt.length > 10 && (
                                        <tr>
                                          <td colSpan="4" className="px-3 py-2 text-center text-slate-400 text-xs">
                                            + {filteredAtt.length - 10} more records
                                          </td>
                                        </tr>
                                      )}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                              {filteredAtt.length === 0 && (
                                <p className="text-xs text-slate-400 mt-2">No attendance records for this period.</p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}